/**
 * 路由表 → 菜单信息（名称、图标）的索引，按 routeMenuData 的数组引用缓存。
 *
 * src/app.tsx 的 menuDataRender 每次 ProLayout 重渲染都会调用本模块，若逐节点线性
 * 扫全表就是 O(菜单数 × 路由数)；这里建一次 Map，查找降为 O(1)。
 * 遍历顺序为「先看节点自身 → routes 子树 → children 子树」，且先出现先登记、不覆盖，
 * 与逐节点查找的命中结果一致。以引用为键，路由配置变化（含 HMR）得到新数组会自行重建。
 */
type RouteMenuInfo = { name?: unknown; icon?: unknown };

let routeMenuIndexCache: {
  source: any[];
  index: Map<string, RouteMenuInfo>;
} | null = null;

function getRouteMenuIndex(routeMenuData: any[]): Map<string, RouteMenuInfo> {
  if (routeMenuIndexCache && routeMenuIndexCache.source === routeMenuData) {
    return routeMenuIndexCache.index;
  }
  const index = new Map<string, RouteMenuInfo>();
  const walk = (routes: any[]): void => {
    for (const route of routes) {
      if (!route) continue;
      // 保持「第一次出现即为最终结果」的原语义：已登记过的 path 不再覆盖。
      if (route.path && !index.has(route.path)) {
        index.set(route.path, { name: route.name, icon: route.icon });
      }
      if (route.routes && route.routes.length > 0) walk(route.routes);
      if (route.children && route.children.length > 0) walk(route.children);
    }
  };
  walk(routeMenuData);
  routeMenuIndexCache = { source: routeMenuData, index };
  return index;
}

/**
 * 从路由配置中查找菜单信息（名称、图标等）。
 * 保留原函数名与签名，调用方无需改动。
 */
function findRouteMenuInfo(routeMenuData: any[], path: string): any {
  if (!routeMenuData || !path) {
    return null;
  }
  const info = getRouteMenuIndex(routeMenuData).get(path);
  return info || null;
}

/**
 * 清理菜单名称：移除英文标识符（如 "user 个人页" -> "个人页"）
 */
function cleanMenuName(name: string): string {
  if (!name) {
    return '';
  }
  
  // 如果名称包含空格，且第一个单词是英文标识符，移除它
  const match = name.match(/^([a-zA-Z-]+)\s+(.+)$/);
  if (match) {
    // 第二个部分是中文名称
    return match[2].trim();
  }
  
  return name.trim();
}

/**
 * 将后端菜单数据转换为ProLayout需要的格式，并合并路由配置中的图标和名称
 * ProLayout菜单格式: { key, name, path, icon, children? }
 * 后端菜单格式: { id, name, code, icon, path, component, isVirtual, parentId, children, ... }
 * 
 * 菜单布局规则：
 * - 一级菜单：显示在左侧侧边栏，点击时弹出二级/三级菜单面板
 * - 二级菜单：作为弹出面板中的分组标题（使用groupTitle属性）
 * - 三级菜单：作为分组内的可点击菜单项（真正的路由跳转）
 */
export function convertBackendMenusToProLayout(menus: any[], routeMenuData: any[], parentI18nKey?: string, level: number = 1): any[] {
  if (!menus || menus.length === 0) {
    return [];
  }

  return menus
    .map((menu) => {
      if (!menu) {
        return null;
      }

      // 判断是否为虚拟节点（isVirtual可能是boolean、number或undefined）
      const isVirtual = menu.isVirtual === true || menu.isVirtual === 1 || menu.isVirtual === '1';
      
      // 判断是否有子菜单
      const hasSourceChildren = Array.isArray(menu.children) && menu.children.length > 0;

      // 从路由配置中查找菜单信息（优先使用路由配置中的名称和图标）
      const routeInfo = menu.path ? findRouteMenuInfo(routeMenuData, menu.path) : null;
      
      // 构建基础菜单项
      const menuItem: any = {
        key: menu.path || menu.code || menu.id?.toString() || `menu-${menu.id}`,
        // 🔑 关键修复：确保所有菜单项都有 children 属性
        // 避免渲染时因 children 为 undefined 导致的问题
        children: [],
      };

      // 保留后端菜单 code：T5 侧栏分组的第一优先级匹配键（code 精确匹配）。
      // 一级菜单的 code 形如 system / sales / procurement，与 path 前缀互为兜底。
      if (menu.code) {
        menuItem.code = menu.code;
      }
      
      // 🔑 菜单名称处理逻辑：
      // 1. 如果路由配置中有 name (routeInfo?.name)，优先使用它（ProLayout 会自动从国际化文件中查找）
      // 2. 如果路由配置没有 name，则根据后端菜单名称 (menu.name) 进行判断：
      //    a. 如果后端名称本身就是国际化 Key (以 'menu.' 开头)，直接使用。
      //    b. 如果后端名称包含中文或点号，认为是直接显示名称，直接使用。
      //    c. 如果后端名称是通用词 (如 'List', 'Card', 'Detail') 且父级有国际化 Key，则继承父级 Key。
      //    d. 否则，使用清理后的后端名称 (cleanMenuName) 作为 fallback。
      // 🔑 DB 为主，i18n 作 fallback：菜单管理页改 DB name 后侧边栏即时生效
      const resolvedName = (() => {
        const backendMenuName = (menu.name || '').trim();
        const cleanedBackendMenuName = cleanMenuName(backendMenuName);

        // ① DB 有中文显示名 → 直接用（最优先，统一数据源）
        if (backendMenuName && /[\u4e00-\u9fa5]/.test(backendMenuName)) {
          return backendMenuName;
        }
        // ② DB 名本身就是 i18n key（menu.xxx）→ 直接用让 ProLayout 翻译
        if (backendMenuName.startsWith('menu.')) {
          return backendMenuName;
        }
        // ③ 退回路由配置里的 i18n key
        if (routeInfo?.name) {
          return routeInfo.name;
        }
        // ④ List/Card/Detail 通用名 → 继承父级 i18n key
        if (parentI18nKey && (cleanedBackendMenuName === 'List' || cleanedBackendMenuName === 'Card' || cleanedBackendMenuName === 'Detail')) {
          return parentI18nKey;
        }
        // ⑤ 最终 fallback
        return cleanedBackendMenuName || menu.code || '';
      })();

      // 如果有子菜单，递归转换
      if (hasSourceChildren) {
        // 🔑 重新计算用于子菜单继承的国际化 Key
        let currentMenuI18nKey: string | undefined = undefined;
        if (routeInfo?.name) {
          currentMenuI18nKey = routeInfo.name; // 从路由配置中获取最可靠的国际化 Key
        } else if (typeof resolvedName === 'string' && resolvedName.startsWith('menu.')) {
          currentMenuI18nKey = resolvedName; // 如果resolvedName本身就是国际化key
        } else if (parentI18nKey) {
          // 如果当前菜单没有明确的i18n key，但是父级有，则尝试传递父级的key
          // 这通常发生在父级是一个虚拟节点，或者只有后端名称，而实际的i18n key在更高层级定义
          currentMenuI18nKey = parentI18nKey;
        }

        const convertedChildren = convertBackendMenusToProLayout(menu.children, routeMenuData, currentMenuI18nKey, level + 1);
        // 始终设置children属性，确保ProLayout识别为SubMenu
        menuItem.children = convertedChildren.length > 0 ? convertedChildren : [];
      }

      // 权限动作只参与授权，不进入导航；按过滤后的子项判断分组，避免页面变成空分组。
      const hasChildren = menuItem.children.length > 0;
      if (isVirtual && !hasChildren && !menu.path) return null;

      // 设置图标：只有一级菜单显示图标
      if (level === 1) {
        menuItem.name = resolvedName;
        if (routeInfo?.icon) {
          menuItem.icon = routeInfo.icon;
        } else if (menu.icon) {
          menuItem.icon = menu.icon;
        }
        // 🖱️ 关键：一级菜单必须确保有children属性
        // 这样点击时才能弹出子菜单面板
        // 始终设置children属性，确保点击时能弹出面板
        if (!menuItem.children) {
          menuItem.children = [];
        }
      } else if (level === 2) {
        // 二级菜单：
        // - 虚拟节点（isVirtual: true）：作为分组标题，只有当有子菜单时才显示
        // - 非虚拟节点（isVirtual: false）：作为可点击菜单项，即使没有子菜单也显示
        // 保留原二级虚拟空叶隐藏规则（如仅用于授权的流程设计器入口）。
        if (isVirtual && !hasChildren) return null;

        menuItem.groupTitle = resolvedName;
        menuItem.name = resolvedName;
        // 设置key确保唯一性
        menuItem.key = `group-${menu.id}-${menu.code}`;
        // 如果有path且不是虚拟节点，设置path用于跳转
        if (menu.path && !isVirtual) {
          menuItem.path = menu.path;
        }
        // 确保二级菜单有children属性
        if (!menuItem.children) {
          menuItem.children = [];
        }
      } else if (level === 3) {
        // 三级菜单：作为可点击菜单项
        menuItem.name = resolvedName;
        // 三级菜单：必须设置path用于跳转
        if (menu.path) {
          menuItem.path = menu.path;
        }
        // 设置key确保唯一性
        menuItem.key = menu.path || `item-${menu.id}-${menu.code}`;
      } else {
        // 四级及以上菜单：作为普通菜单项
        menuItem.name = resolvedName;
        if (menu.path) {
          menuItem.path = menu.path;
        }
        menuItem.key = menu.path || `item-${menu.id}-${menu.code}`;
      }

      // 设置路径：
      // - 一级菜单：如果没有子菜单且不是虚拟节点，可以有path（直接跳转）
      if (level === 1) {
        if (!hasChildren && !isVirtual && menu.path) {
          menuItem.path = menu.path;
        }
        // 有子菜单的一级菜单不设置path，作为弹出菜单的触发器
      }


      return menuItem;
    })
    .filter((menu) => menu !== null && menu !== undefined);
}

